/**
 * AddressesDynamicTab — F12.N T5.4.
 *
 * Dynamic rendering zakladki Adresy oparty o:
 *  - layout helper-encji `addresses` (z `useSystemModuleActiveLayout('addresses')`)
 *  - liste pol helper-encji (`/api/system-modules/addresses/fields`)
 *  - slownik `address_kinds` (`useDictionary`)
 *
 * Zastepuje statyczny `AddressesTab.tsx` (456 linii, hardcoded ADDRESS_KINDS +
 * hardcoded kolumny tabeli + hardcoded form fields). Backend `/api/addresses`
 * bez zmian (polymorphic ownerType: company / employee / contractor).
 *
 * Public API 1:1 ze starym: `{ ownerType, ownerId, testIdPrefix? }`.
 *
 * Mapowanie snake_case ↔ camelCase: slugi pol w `system_module_fields` sa
 * snake_case (`is_primary`, `postal_code`), API response — camelCase. Helper
 * `slugToCamel` w `lib/slug-to-camel.ts` (z unit testem).
 *
 * Pole z `dictionaryCode='address_kinds'` (czyli `kind`) renderuje sie jako
 * <select> z opcjami z aktywnych pozycji slownika. Tabela pokazuje label
 * slownikowy (z fallbackiem na raw code gdy item zostal deactivated).
 */

import { useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin } from 'lucide-react';

import {
  type AddressCoreField,
  AddressCoreFields,
  type AddressCoreFieldsValue,
} from '@/components/addresses/AddressCoreFields';
import { type AddressRow, buildAddressColumns } from '@/components/addresses/addresses-columns';
import { Button } from '@/components/Button';
import { ConfigurableTable } from '@/components/configurable-table';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  type LayoutConfig,
  type LayoutElement,
  RecursiveFormRenderer,
} from '@/components/form/RecursiveFormRenderer';
import { Modal } from '@/components/Modal';
import { Skeleton } from '@/components/Skeleton';
import { ExtFieldValueInput } from '@/components/system-modules/ExtFieldValueInput';
import { useToast } from '@/contexts/ToastContext';
import { type DictionaryItem, useDictionary } from '@/hooks/useDictionary';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { api } from '@/lib/api';
import { applyOrder } from '@/lib/kartoteki/formatka-order';
import { slugToCamel } from '@/lib/slug-to-camel';

const MODULE_SLUG = 'addresses';
const ADDRESS_KINDS_DICTIONARY = 'address_kinds';

/** Mapa pole core adresu (camelCase) → slug w katalogu `system_module_fields` (snake_case). */
const ADDRESS_CORE_FIELD_SLUG: Record<AddressCoreField, string> = {
  countryCode: 'country_code',
  street: 'street',
  buildingNo: 'building_no',
  apartmentNo: 'apartment_no',
  postalCode: 'postal_code',
  city: 'city',
  region: 'region',
};

export type AddressOwnerType = 'company' | 'contractor' | 'employee';

interface SystemModuleField {
  id: string;
  slug: string;
  name: string;
  fieldType: string;
  isCore: boolean;
  isRequired: boolean;
  description: string | null;
  showInList: boolean;
  options: Array<{ value: string; label: string; color?: string }> | null;
  dictionaryCode: string | null;
  /** Nadpisanie sposobu wyświetlania pozycji słownika dla TEGO pola (Etap 1c) —
   *  `null`/`undefined` = użyj `dictionary.displayMode` (fallback). */
  dictionaryDisplayMode?: 'label' | 'code' | 'code_label' | null;
  gcrecord: string | null;
}

interface FieldsResponse {
  fields: SystemModuleField[];
}

export interface AddressesDynamicTabProps {
  ownerType: AddressOwnerType;
  ownerId: string;
  /** Custom testid prefix (default: `addresses`). */
  testIdPrefix?: string;
  /** Slugi kolumn ukrytych z layoutu (formatka bloku `contractor_addresses_view`, ikonka oka). */
  hiddenColumnIds?: readonly string[];
  /** Kolejność kolumn (slugi) z formatki bloku — drag; nieujęte kolumny na końcu (back-compat). */
  columnOrder?: readonly string[];
}

export function AddressesDynamicTab({
  ownerType,
  ownerId,
  testIdPrefix = 'addresses',
  hiddenColumnIds,
  columnOrder,
}: AddressesDynamicTabProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();

  const queryKey = ['addresses', ownerType, ownerId];

  // Spójność adresu macierzystego: adres główny kontrahenta to ten sam wiersz, który
  // zakładka „Dane" trzyma w `form.primaryAddress`. Po zmianie adresu tutaj odświeżamy
  // też kartę właściciela, żeby „Dane" nie pokazywały „ducha" (stary/skasowany adres).
  function invalidateOwnerDetail() {
    if (ownerType === 'contractor') {
      queryClient.invalidateQueries({ queryKey: ['contractor', ownerId] });
    }
  }

  // ── Fetchy ───────────────────────────────────────────────────────────────
  const addressesQ = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await api.get<{ addresses: AddressRow[] }>('/addresses', {
        params: { ownerType, ownerId },
      });
      return res.data.addresses;
    },
  });

  const fieldsQ = useQuery({
    queryKey: ['system-module-fields', MODULE_SLUG],
    queryFn: async () => {
      const res = await api.get<FieldsResponse>(`/system-modules/${MODULE_SLUG}/fields`);
      return res.data.fields;
    },
    staleTime: 2 * 60 * 1000,
  });

  const {
    layoutConfig,
    isLoading: layoutLoading,
    isError: layoutError,
  } = useSystemModuleActiveLayout(MODULE_SLUG);
  const {
    dictionary: kindDictionary,
    activeItems: kindItems,
    itemsByCode: kindByCode,
    isLoading: dictLoading,
  } = useDictionary(ADDRESS_KINDS_DICTIONARY);

  // ── Modal state ──────────────────────────────────────────────────────────
  const formId = useId();
  const [showModal, setShowModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState<AddressRow | null>(null);
  const [formValues, setFormValues] = useState<Record<string, unknown>>({});
  // F12.N T5.4 follow-up: mapa per-pole bledow walidacji z backendu. Klucz =
  // camelCase nazwa pola w API response (np. `countryCode`), wartosc = komunikat
  // do wyswietlenia inline pod inputem.
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);

  // ── Maps + helper ────────────────────────────────────────────────────────
  const fieldById = useMemo(() => {
    const map = new Map<string, SystemModuleField>();
    for (const f of fieldsQ.data ?? []) map.set(f.id, f);
    return map;
  }, [fieldsQ.data]);

  // Etap 10.3 (ADR-0066 §11) — `formValues` jest kluczowany CAMELCASE
  // (`slugToCamel`), a `dictionaryParamBindings` wskazuje slug-sąsiada RAW (z
  // `system_module_fields.slug`) — bez tego mostka lookup w `ExtFieldValueInput`
  // nie znalazłby wartości sąsiada dla pola param-bound tej helper-encji.
  const recordValuesBySlug = useMemo(() => {
    const out: Record<string, unknown> = {};
    for (const f of fieldsQ.data ?? []) {
      if (f.gcrecord !== null) continue;
      out[f.slug] = formValues[slugToCamel(f.slug)];
    }
    return out;
  }, [fieldsQ.data, formValues]);

  // Zawartosc zakladki `data` z layoutu (rozpakowujemy tabs wrapper — modal
  // nie ma sensu pokazywac paska zakladek dla helper-encji z jednym tabem).
  const formLayoutConfig: LayoutConfig | null = useMemo(() => {
    if (!layoutConfig) return null;
    const tabsEl = layoutConfig.elements.find((el) => el.type === 'tabs');
    const tabs = tabsEl?.tabs ?? [];
    const dataTab = tabs.find((t) => t.slug === 'data') ?? tabs[0];
    if (!dataTab) return { elements: [] };
    return { elements: dataTab.children };
  }, [layoutConfig]);

  // ── Ujednolicony formularz adresu (WF NEW 2026-08-20) ───────────────────────
  // 7 pól core adresu renderuje wspólny `AddressCoreFields` (ten sam komponent i te
  // same etykiety co zakładka „Dane" kontrahenta), Z BRAMKĄ: Województwo tylko dla
  // Polski. Pola kind/label/isPrimary/notes (poza katalogiem core) zostają
  // data-driven przez `renderField`/layout jak dotychczas.
  const addressCoreFieldIds = useMemo(() => {
    const slugs = new Set(Object.values(ADDRESS_CORE_FIELD_SLUG));
    const ids = new Set<string>();
    for (const f of fieldsQ.data ?? []) {
      if (slugs.has(f.slug)) ids.add(f.id);
    }
    return ids;
  }, [fieldsQ.data]);

  // Sekcja layoutu złożona WYŁĄCZNIE z pól core adresu (realny seed: sekcja „Adres")
  // → w tym miejscu wstawiamy AddressCoreFields, zachowując oryginalną kolejność
  // sekcji. Gdy layout (np. mock testowy) nie ma takiej dedykowanej sekcji —
  // AddressCoreFields dorzucamy na końcu, a pojedyncze pola core są po prostu
  // wycięte z sekcji w których się znalazły (reszta pól tej sekcji zostaje).
  const { layoutBeforeAddress, layoutAfterAddress, addressSectionSettings } = useMemo(() => {
    const elements = formLayoutConfig?.elements ?? [];
    const idx = elements.findIndex((el) => isFullyCoreAddressSection(el, addressCoreFieldIds));
    if (idx === -1) {
      return {
        layoutBeforeAddress: filterOutFieldIds(elements, addressCoreFieldIds),
        layoutAfterAddress: [] as LayoutElement[],
        addressSectionSettings: null as { title?: string; columns?: number } | null,
      };
    }
    return {
      layoutBeforeAddress: filterOutFieldIds(elements.slice(0, idx), addressCoreFieldIds),
      layoutAfterAddress: filterOutFieldIds(elements.slice(idx + 1), addressCoreFieldIds),
      addressSectionSettings: (elements[idx]!.settings ?? {}) as {
        title?: string;
        columns?: number;
      },
    };
  }, [formLayoutConfig, addressCoreFieldIds]);

  const addressCoreValue: AddressCoreFieldsValue = useMemo(
    () => ({
      countryCode: (formValues.countryCode as string | null) ?? null,
      street: (formValues.street as string | null) ?? null,
      buildingNo: (formValues.buildingNo as string | null) ?? null,
      apartmentNo: (formValues.apartmentNo as string | null) ?? null,
      postalCode: (formValues.postalCode as string | null) ?? null,
      city: (formValues.city as string | null) ?? null,
      region: (formValues.region as string | null) ?? null,
    }),
    [formValues],
  );

  const addressCoreErrors = useMemo(() => {
    const out: Partial<Record<AddressCoreField, string>> = {};
    for (const field of Object.keys(ADDRESS_CORE_FIELD_SLUG) as AddressCoreField[]) {
      if (formErrors[field]) out[field] = formErrors[field];
    }
    return out;
  }, [formErrors]);

  function handleAddressCoreChange(next: AddressCoreFieldsValue) {
    setFormValues((prev) => ({ ...prev, ...next }));
    setFormErrors((prev) => {
      const nextErrors = { ...prev };
      for (const field of Object.keys(next) as AddressCoreField[]) {
        if (next[field] !== addressCoreValue[field]) delete nextErrors[field];
      }
      return nextErrors;
    });
  }

  // Lista pol z `showInList=true` w kolejnosci wystepowania w layoucie (DFS).
  const listColumns = useMemo<SystemModuleField[]>(() => {
    if (!formLayoutConfig) return [];
    const hidden = new Set(hiddenColumnIds ?? []);
    const out: SystemModuleField[] = [];
    walkFieldsInLayout(formLayoutConfig.elements, (fieldId) => {
      const f = fieldById.get(fieldId);
      // Ukrywanie kolumn z layoutu (formatka bloku) — pomijamy pola na hide-liście.
      if (f && f.gcrecord === null && f.showInList && !hidden.has(f.slug)) out.push(f);
    });
    return out;
  }, [formLayoutConfig, fieldById, hiddenColumnIds]);

  // Kolejność kolumn z formatki bloku (drag) — `applyOrder` układa `listColumns` wg
  // `columnOrder` (nieujęte na końcu, w oryginalnej kolejności). Read-side == builder.
  const orderedListColumns = useMemo<SystemModuleField[]>(() => {
    if (!columnOrder || columnOrder.length === 0) return listColumns;
    const bySlug = new Map(listColumns.map((c) => [c.slug, c]));
    return applyOrder(
      listColumns.map((c) => c.slug),
      columnOrder,
    )
      .map((slug) => bySlug.get(slug))
      .filter((c): c is SystemModuleField => !!c);
  }, [listColumns, columnOrder]);

  const tableColumns = useMemo(
    () =>
      buildAddressColumns({
        listColumns: orderedListColumns,
        kindByCode,
        kindDictionary,
        testIdPrefix,
        t,
      }),
    [orderedListColumns, kindByCode, kindDictionary, testIdPrefix, t],
  );

  // ── Open/close ───────────────────────────────────────────────────────────
  function openCreate() {
    setEditingAddress(null);
    setFormValues(buildInitialFormValues(fieldsQ.data ?? [], kindItems));
    setFormErrors({});
    setShowModal(true);
  }

  function openEdit(addr: AddressRow) {
    setEditingAddress(addr);
    setFormValues(addressRowToFormValues(addr, fieldsQ.data ?? []));
    setFormErrors({});
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setFormErrors({});
  }

  // ── Mutations ────────────────────────────────────────────────────────────
  const saveMutation = useMutation({
    mutationFn: async () => {
      setFormErrors({});
      const payload = buildPayload(formValues, fieldsQ.data ?? []);
      if (editingAddress) {
        // PATCH nie przyjmuje ownerType/ownerId.
        const { ownerType: _ot, ownerId: _oi, ...patch } = { ...payload, ownerType, ownerId };
        const res = await api.patch(`/addresses/${editingAddress.id}`, patch);
        return res.data;
      }
      const res = await api.post('/addresses', { ...payload, ownerType, ownerId });
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      invalidateOwnerDetail();
      toast.show({
        variant: 'success',
        title: t(
          editingAddress ? 'tenant.addresses.toast.updated' : 'tenant.addresses.toast.created',
        ),
      });
      closeModal();
    },
    onError: (err: unknown) => {
      const apiErr = extractApiError(err);
      if (apiErr.code === 'PRIMARY_ADDRESS_PROTECTED') {
        toast.show({ variant: 'error', title: t('tenant.addresses.toast.primaryProtected') });
        return;
      }
      if (apiErr.code === 'VALIDATION_ERROR' && apiErr.details) {
        // Map: { countryCode: ["string must contain exactly 2..."] } → { countryCode: "Wymagane 2 znaki" }
        const fieldErrors: Record<string, string> = {};
        for (const [key, msgs] of Object.entries(apiErr.details)) {
          if (Array.isArray(msgs) && msgs.length > 0) {
            fieldErrors[key] = translateValidationMessage(msgs[0]!);
          }
        }
        setFormErrors(fieldErrors);
        toast.show({ variant: 'error', title: t('tenant.addresses.toast.validationError') });
        return;
      }
      if (apiErr.code === 'INVALID_KIND') {
        setFormErrors({ kind: t('tenant.addresses.fieldError.invalidKind') });
        toast.show({ variant: 'error', title: t('tenant.addresses.toast.invalidKind') });
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.addresses.toast.saveError') });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (addressId: string) => {
      const res = await api.delete(`/addresses/${addressId}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      invalidateOwnerDetail();
      toast.show({ variant: 'success', title: t('tenant.addresses.toast.deleted') });
    },
    onError: (err: unknown) => {
      const apiErr = extractApiError(err);
      if (apiErr.code === 'PRIMARY_ADDRESS_PROTECTED') {
        toast.show({ variant: 'error', title: t('tenant.addresses.toast.primaryProtected') });
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.addresses.toast.deleteError') });
    },
  });

  // ── Render: loading guard ────────────────────────────────────────────────
  const initialLoading = addressesQ.isLoading || fieldsQ.isLoading || layoutLoading || dictLoading;

  if (initialLoading) {
    return (
      <div className="space-y-3 p-2" data-testid={`${testIdPrefix}-loading`}>
        <Skeleton className="h-8" />
        <Skeleton className="h-24" />
      </div>
    );
  }

  if (addressesQ.isError) {
    return <p className="text-sm text-red-600 py-4">{t('tenant.addresses.error')}</p>;
  }

  if (layoutError || !formLayoutConfig || formLayoutConfig.elements.length === 0) {
    return (
      <p className="text-sm text-amber-600 py-4" data-testid={`${testIdPrefix}-no-layout`}>
        {t('tenant.addresses.errorLayout')}
      </p>
    );
  }

  // WF NEW 2026-08-13 (punkt 1) — adres macierzysty (siedziba/rejestrowy = główny `office`)
  // na górze listy: najpierw `isPrimary`, w obrębie tego najpierw typ `office`. Reszta w
  // dotychczasowej kolejności (stabilny sort — zachowuje kolejność serwera dla równych).
  const PRIMARY_KIND = 'office';
  const addresses = [...(addressesQ.data ?? [])].sort((a, b) => {
    const rank = (r: AddressRow) =>
      (r.isPrimary ? 0 : 2) + (r.isPrimary && r.kind === PRIMARY_KIND ? -1 : 0);
    return rank(a) - rank(b);
  });

  // ── renderField callback dla RecursiveFormRenderer ──────────────────────
  function renderField(fieldId: string) {
    const field = fieldById.get(fieldId);
    if (!field || field.gcrecord !== null) return null;
    const camelKey = slugToCamel(field.slug);
    const value = formValues[camelKey];
    const error = formErrors[camelKey];

    // Inject slownik dla pol `select` z dictionaryCode='address_kinds'.
    const effectiveField: SystemModuleField =
      field.dictionaryCode === ADDRESS_KINDS_DICTIONARY
        ? { ...field, options: kindItems.map(itemToFieldOption) }
        : field;

    const errId = error ? `${testIdPrefix}-form-${field.slug}-error` : undefined;
    return (
      <FieldWrap
        key={fieldId}
        label={field.name}
        required={field.isRequired}
        error={error}
        errId={errId}
        colSpan={field.fieldType === 'text'}
      >
        <ExtFieldValueInput
          field={effectiveField}
          value={value}
          onChange={(v) => {
            setFormValues((prev) => ({ ...prev, [camelKey]: v }));
            if (error) {
              setFormErrors((prev) => {
                const next = { ...prev };
                delete next[camelKey];
                return next;
              });
            }
          }}
          error={error}
          aria-describedby={errId}
          data-testid={`${testIdPrefix}-form-${field.slug}`}
          recordValues={recordValuesBySlug}
        />
      </FieldWrap>
    );
  }

  function renderRowActions(addr: AddressRow) {
    return (
      <div className="flex justify-end gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openEdit(addr)}
          className="rounded px-2 py-0.5 text-xs text-text-tertiary"
          data-testid={`${testIdPrefix}-edit-${addr.id}`}
        >
          {t('common.edit', { defaultValue: 'Edytuj' })}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setPendingDeleteId(addr.id)}
          className="rounded px-2 py-0.5 text-xs text-red-500"
          data-testid={`${testIdPrefix}-delete-${addr.id}`}
        >
          {t('common.delete', { defaultValue: 'Usuń' })}
        </Button>
      </div>
    );
  }

  // ── Render glowny ────────────────────────────────────────────────────────
  return (
    <div className="space-y-3" data-testid={testIdPrefix}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-text-primary">{t('tenant.addresses.title')}</span>
        <Button
          variant="primary"
          size="sm"
          onClick={openCreate}
          className="text-xs"
          data-testid={`${testIdPrefix}-add-btn`}
        >
          {t('tenant.addresses.addBtn')}
        </Button>
      </div>

      <ConfigurableTable<AddressRow>
        tableId={`addresses-tab-${ownerType}`}
        testIdPrefix={testIdPrefix}
        columns={tableColumns}
        data={addresses}
        persistConfig={false}
        showViewModeToggle={false}
        compact
        stickyHeader={false}
        renderActions={renderRowActions}
        emptyIcon={MapPin}
        emptyTitle={t('tenant.addresses.empty')}
      />

      <Modal
        open={showModal}
        onClose={closeModal}
        title={t(
          editingAddress ? 'tenant.addresses.modal.editTitle' : 'tenant.addresses.modal.newTitle',
        )}
        size="lg"
        testId={`${testIdPrefix}-modal`}
        // Zapis w toku — tło i Esc nie zamykają okna (X i „Anuluj" działają).
        closeOnBackdrop={!saveMutation.isPending}
        closeOnEsc={!saveMutation.isPending}
        footer={
          <>
            <Button variant="ghost" onClick={closeModal}>
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form={formId}
              variant="primary"
              loading={saveMutation.isPending}
              disabled={saveMutation.isPending}
              data-testid={`${testIdPrefix}-form-submit`}
            >
              {saveMutation.isPending ? t('common.saving') : t('common.save')}
            </Button>
          </>
        }
      >
        <form
          id={formId}
          data-testid={`${testIdPrefix}-form`}
          onSubmit={(e) => {
            e.preventDefault();
            saveMutation.mutate();
          }}
          className="space-y-3"
        >
          <RecursiveFormRenderer
            layoutConfig={{ elements: layoutBeforeAddress }}
            renderField={renderField}
            formData={formValues}
          />

          <div className="rounded-lg border border-border-secondary bg-bg-primary p-4">
            {addressSectionSettings?.title && (
              <h3 className="mb-3 text-sm font-semibold text-text-primary">
                {addressSectionSettings.title}
              </h3>
            )}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <AddressCoreFields
                value={addressCoreValue}
                onChange={handleAddressCoreChange}
                editable
                errors={addressCoreErrors}
                testId={(field) => `${testIdPrefix}-form-${ADDRESS_CORE_FIELD_SLUG[field]}`}
              />
            </div>
          </div>

          <RecursiveFormRenderer
            layoutConfig={{ elements: layoutAfterAddress }}
            renderField={renderField}
            formData={formValues}
          />

          {Object.keys(formErrors).length > 0 && (
            <div
              role="alert"
              className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300"
              data-testid={`${testIdPrefix}-form-errors-banner`}
            >
              {t('tenant.addresses.validationBanner')}
            </div>
          )}
        </form>
      </Modal>

      <ConfirmDialog
        open={pendingDeleteId !== null}
        title={t('common.confirmDelete')}
        message={t('tenant.addresses.deleteConfirm')}
        onConfirm={() => {
          const id = pendingDeleteId;
          setPendingDeleteId(null);
          if (id) deleteMutation.mutate(id);
        }}
        onClose={() => setPendingDeleteId(null)}
      />
    </div>
  );
}

// ─── Pomocnicze: FieldWrap ─────────────────────────────────────────────────

function FieldWrap({
  label,
  required,
  colSpan,
  error,
  errId,
  children,
}: {
  label: string;
  required?: boolean;
  colSpan?: boolean;
  error?: string;
  errId?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={colSpan ? 'col-span-2' : undefined}>
      <label className="block text-xs font-medium text-text-tertiary mb-1">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
      </label>
      {children}
      {error && (
        <p id={errId} className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

// ─── Helpery ─────────────────────────────────────────────────────────────

/** Czy `el` jest sekcją złożoną WYŁĄCZNIE z pól core adresu (realny seed: sekcja „Adres"). */
function isFullyCoreAddressSection(el: LayoutElement, coreFieldIds: Set<string>): boolean {
  if (el.type !== 'section' || !el.children || el.children.length === 0) return false;
  return el.children.every(
    (child) => child.type === 'field' && !!child.fieldId && coreFieldIds.has(child.fieldId),
  );
}

/** Usuwa rekurencyjnie elementy `type='field'` wskazujące na pola core adresu (te renderuje
 *  `AddressCoreFields` osobno) — reszta drzewa (sekcje/pola/tabs) zostaje nietknięta. */
function filterOutFieldIds(elements: LayoutElement[], excludeIds: Set<string>): LayoutElement[] {
  const out: LayoutElement[] = [];
  for (const el of elements) {
    if (el.type === 'field') {
      if (el.fieldId && excludeIds.has(el.fieldId)) continue;
      out.push(el);
      continue;
    }
    if (el.children) {
      out.push({ ...el, children: filterOutFieldIds(el.children, excludeIds) });
      continue;
    }
    if (el.tabs) {
      out.push({
        ...el,
        tabs: el.tabs.map((tab) => ({
          ...tab,
          children: filterOutFieldIds(tab.children, excludeIds),
        })),
      });
      continue;
    }
    out.push(el);
  }
  return out;
}

function walkFieldsInLayout(elements: LayoutElement[], visit: (fieldId: string) => void): void {
  for (const el of elements) {
    if (el.type === 'field' && el.fieldId) {
      visit(el.fieldId);
    }
    if (el.children) walkFieldsInLayout(el.children, visit);
    if (el.tabs) {
      for (const tab of el.tabs) walkFieldsInLayout(tab.children, visit);
    }
    if (el.accordionItems) {
      for (const item of el.accordionItems) walkFieldsInLayout(item.children, visit);
    }
    if (el.steps) {
      for (const step of el.steps) walkFieldsInLayout(step.children, visit);
    }
  }
}

function itemToFieldOption(item: DictionaryItem): { value: string; label: string; color?: string } {
  return {
    value: item.code,
    label: item.label,
    ...(item.color ? { color: item.color } : {}),
  };
}

function buildInitialFormValues(
  fields: SystemModuleField[],
  kindItems: DictionaryItem[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.gcrecord !== null) continue;
    const key = slugToCamel(f.slug);
    if (f.fieldType === 'boolean') {
      out[key] = false;
    } else if (f.dictionaryCode === ADDRESS_KINDS_DICTIONARY) {
      // Default = pierwsza aktywna pozycja slownika (UX: dropdown ma sensowna
      // wartosc poczatkowa zamiast pustej, ktora nie przeszlaby walidacji backendu).
      out[key] = kindItems[0]?.code ?? null;
    } else {
      out[key] = null;
    }
  }
  // Domyslne wartosci ktorych helper-encja nie ma w polach (np. isActive=true).
  if (!('isActive' in out)) out.isActive = true;
  return out;
}

function addressRowToFormValues(
  addr: AddressRow,
  fields: SystemModuleField[],
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  const asAny = addr as unknown as Record<string, unknown>;
  for (const f of fields) {
    if (f.gcrecord !== null) continue;
    const key = slugToCamel(f.slug);
    out[key] = asAny[key] ?? (f.fieldType === 'boolean' ? false : null);
  }
  // Pola spoza helper-encji (rowniez wysylane do API) — kopiuj raw.
  if (!('isActive' in out)) out.isActive = addr.isActive ?? true;
  return out;
}

function buildPayload(
  formValues: Record<string, unknown>,
  fields: SystemModuleField[],
): Record<string, unknown> {
  // Wez tylko klucze ktore odpowiadaja polom helper-encji + jawnym extra (isActive).
  const allowedKeys = new Set<string>(['isActive']);
  for (const f of fields) {
    if (f.gcrecord !== null) continue;
    allowedKeys.add(slugToCamel(f.slug));
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(formValues)) {
    if (!allowedKeys.has(k)) continue;
    out[k] = v === '' ? null : v;
  }
  return out;
}

interface ApiError {
  code: string | null;
  /** Map: field-key (camelCase) → array of Zod messages. Obecne dla VALIDATION_ERROR. */
  details?: Record<string, string[]>;
}

function extractApiError(err: unknown): ApiError {
  if (!err || typeof err !== 'object') return { code: null };
  const maybe = err as {
    response?: { data?: { error?: string; details?: Record<string, string[]> } };
  };
  return {
    code: maybe.response?.data?.error ?? null,
    details: maybe.response?.data?.details,
  };
}

/**
 * Tłumaczenie najczęstszych komunikatów Zod (po angielsku domyślnie) na polski.
 * Pokrywa walidacje uzywane w POST/PATCH /api/addresses:
 *  - exactly N character (countryCode = 2 znaki)
 *  - String must contain at most N character (label/street/buildingNo/... max)
 *  - String must contain at least N character (min)
 *  - Required (pole wymagane)
 *  - Invalid email / Invalid url
 *
 * Fallback: raw message Zod (po angielsku) — lepiej niz nic.
 */
function translateValidationMessage(raw: string): string {
  const exactly = raw.match(/exactly (\d+) character/i);
  if (exactly) return `Wymagane dokładnie ${exactly[1]} znaki`;

  const atMost = raw.match(/at most (\d+) character/i);
  if (atMost) return `Maksymalnie ${atMost[1]} znaków`;

  const atLeast = raw.match(/at least (\d+) character/i);
  if (atLeast) return `Minimum ${atLeast[1]} znaków`;

  if (/required/i.test(raw)) return 'Pole wymagane';
  if (/invalid email/i.test(raw)) return 'Nieprawidłowy email';
  if (/invalid url/i.test(raw)) return 'Nieprawidłowy adres URL';
  if (/invalid uuid/i.test(raw)) return 'Nieprawidłowy identyfikator';

  return raw;
}
